import React, {
  useMemo,
} from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { Provider as ReduxProvider } from 'react-redux';
import {
  BrowserRouter,
  Route,
  Routes,
} from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import Cookies from 'universal-cookie';

import { AuthPageLayout, ProtectedPageLayout } from './components/PageLayout';
import {
  AdminAnalyticsPage,
  AdminClassPage,
  AdminCoursePage,
  AdminEnrolmentsPage,
  AdminExamAssignmentPage,
  AdminExamPage,
  AdminExamsPage,
  AdminFollowUpPage,
  AdminGroupsPage,
  AdminInventoryPage,
  AdminInvoicesPage,
  AdminLeadPage,
  AdminNotesPage,
  AdminPayrollPage,
  AdminPermissionPage,
  AdminPermissionsPage,
  AdminPrintingPage,
  AdminReschedulePage,
  AdminStaffPage,
  AdminStaffsPage,
  AdminStudentPage,
  AdminStudentsPage,
  AdminSubjectsPage,
  AdminTimesheetPage,
  AdminTopicsPage,
  AdminVideoPage,
  AdminVideosPage,
  AnalyticsPage,
  DashboardPage,
  ExamsPage,
  LegacyExamsMarkingPage,
  NotesPage,
  NotFoundPage,
  PrelimChemistryMockExamBookingPage,
  PrelimMathsExt1MockExamBookingPage,
  PrelimPhysicsMockExamBookingPage,
  ProfilePage,
  QuizzesPage,
  ResourcesPage,
  SignInPage,
  StaffClassesPage,
  StaffRollPage,
  StudentReschedulePage,
  TeacherClassPage,
  TeacherMarkingPage,
  TrackerPage,
  TutorialsPage,
  VideoPlayerPage,
  VideosPage,
} from './pages';
import { store } from './redux/store';
import getTheme, { ThemeContext, ThemeMode } from './theme';
import { UserGroup } from './types/common';
import useDarkMode from './utils/react/hooks/useDarkMode';

const cookies = new Cookies();

const ThemedApp = () => {
  const browserThemeMode = cookies.get('themeMode');
  // Prior to reading the user's settings, default to the last theme mode
  // experienced by the browser
  const { darkMode, initDarkMode, initLightMode } = useDarkMode(
    browserThemeMode === ThemeMode.Dark,
  );

  const contextValue = useMemo(() => ({ initDarkMode, initLightMode }), []);

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={getTheme(darkMode ? ThemeMode.Dark : ThemeMode.Light)}>
        <MUIThemeProvider theme={getTheme(darkMode ? ThemeMode.Dark : ThemeMode.Light)}>
          <CssBaseline />
          <BrowserRouter>
            <Routes>
              <Route
                path="signin"
                element={(
                  <AuthPageLayout title="Sign In">
                    <SignInPage />
                  </AuthPageLayout>
                )}
              />
              <Route
                path=""
                element={(
                  <ProtectedPageLayout title="Dashboard">
                    <DashboardPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="notes"
                element={(
                  <ProtectedPageLayout title="Notes">
                    <NotesPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="videos"
                element={(
                  <ProtectedPageLayout title="Videos">
                    <VideosPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="video/:subject/:videoId"
                element={(
                  <ProtectedPageLayout title="Videos">
                    <VideoPlayerPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="resources"
                element={(
                  <ProtectedPageLayout title="Resources">
                    <ResourcesPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="exams"
                element={(
                  <ProtectedPageLayout title="Exams">
                    <ExamsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="quizzes"
                element={(
                  <ProtectedPageLayout title="Quizzes">
                    <QuizzesPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="tutorials"
                element={(
                  <ProtectedPageLayout
                    title="Tutorials"
                  >
                    <TutorialsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="tracker"
                element={(
                  <ProtectedPageLayout
                    title="Tracker"
                    authorisedGroups={[UserGroup.Students]}
                  >
                    <TrackerPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="analytics"
                element={(
                  <ProtectedPageLayout
                    title="Analytics"
                    authorisedGroups={[UserGroup.Students, UserGroup.Admins, UserGroup.Teachers]}
                  >
                    <AnalyticsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="analytics/:studentId"
                element={(
                  <ProtectedPageLayout
                    title="Analytics"
                    authorisedGroups={[UserGroup.Students, UserGroup.Admins, UserGroup.Teachers]}
                  >
                    <AnalyticsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="analytics/:subject/:exam/:studentId/:attempt"
                element={(
                  <ProtectedPageLayout
                    title="Analytics"
                    authorisedGroups={[UserGroup.Students, UserGroup.Admins, UserGroup.Teachers]}
                  >
                    <AnalyticsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="analytics/:subject/:exam/:attempt"
                element={(
                  <ProtectedPageLayout
                    title="Analytics"
                    authorisedGroups={[UserGroup.Students, UserGroup.Admins, UserGroup.Teachers]}
                  >
                    <AnalyticsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="profile"
                element={(
                  <ProtectedPageLayout title="Profile">
                    <ProfilePage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="prelim-chemistry-mock-exam"
                element={(
                  <ProtectedPageLayout
                    title="Prelim Chemistry Mock Exam"
                  >
                    <PrelimChemistryMockExamBookingPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="prelim-physics-mock-exam"
                element={(
                  <ProtectedPageLayout
                    title="Prelim Physics Mock Exam"
                  >
                    <PrelimPhysicsMockExamBookingPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="prelim-maths-ext-1-mock-exam"
                element={(
                  <ProtectedPageLayout
                    title="Prelim Maths Ext 1 Mock Exam"
                  >
                    <PrelimMathsExt1MockExamBookingPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/inventory"
                element={(
                  <ProtectedPageLayout
                    title="Inventory"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminInventoryPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/analytics"
                element={(
                  <ProtectedPageLayout
                    title="Analytics"
                    authorisedGroups={[UserGroup.Managers]}
                  >
                    <AdminAnalyticsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/invoices"
                element={(
                  <ProtectedPageLayout
                    title="Invoices"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminInvoicesPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/follow-up"
                element={(
                  <ProtectedPageLayout
                    title="Follow Up"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminFollowUpPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/students"
                element={(
                  <ProtectedPageLayout
                    title="Students"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminStudentsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/student/:studentId"
                element={(
                  <ProtectedPageLayout
                    title="Students"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminStudentPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/staff"
                element={(
                  <ProtectedPageLayout
                    title="Staff"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminStaffsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/staff/:staffId"
                element={(
                  <ProtectedPageLayout
                    title="Staff"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminStaffPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/leads"
                element={(
                  <ProtectedPageLayout
                    title="Enrolments"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminEnrolmentsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/lead/:leadType/:leadId"
                element={(
                  <ProtectedPageLayout
                    title="Leads"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminLeadPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/subjects"
                element={(
                  <ProtectedPageLayout
                    title="Subjects"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminSubjectsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/subjects/:subject"
                element={(
                  <ProtectedPageLayout
                    title="Subjects"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminSubjectsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/course/:subject/:course"
                element={(
                  <ProtectedPageLayout
                    title="Courses"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminCoursePage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/course/:subject/:course/:stream"
                element={(
                  <ProtectedPageLayout
                    title="Courses"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminCoursePage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/classes"
                element={(
                  <ProtectedPageLayout
                    title="Classes"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <StaffClassesPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/class/:subject/:classId"
                element={(
                  <ProtectedPageLayout
                    title="Classes"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminClassPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/groups"
                element={(
                  <ProtectedPageLayout
                    title="Groups"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminGroupsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/video/:subject/:videoId"
                element={(
                  <ProtectedPageLayout
                    title="Videos"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminVideoPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/videos/:subject"
                element={(
                  <ProtectedPageLayout
                    title="Videos"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminVideosPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/videos/:subject/:category"
                element={(
                  <ProtectedPageLayout
                    title="Videos"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminVideosPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/videos/:subject/:category/:topic"
                element={(
                  <ProtectedPageLayout
                    title="Videos"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminVideosPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/notes/:subject"
                element={(
                  <ProtectedPageLayout
                    title="Notes"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminNotesPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/notes/:subject/:category"
                element={(
                  <ProtectedPageLayout
                    title="Notes"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminNotesPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/resources/:subject"
                element={
                  // TODO: Replace with AdminResourcesPage once no longer stub
                  (
                    <ProtectedPageLayout
                      title="Resources"
                      authorisedGroups={[UserGroup.Admins]}
                    >
                      <ResourcesPage />
                    </ProtectedPageLayout>
                  )
                }
              />
              <Route
                path="admin/topics/:subject/:group"
                element={(
                  <ProtectedPageLayout
                    title="Topics"
                    authorisedGroups={[UserGroup.Managers]}
                  >
                    <AdminTopicsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/exams/:subject"
                element={(
                  <ProtectedPageLayout
                    title="Exams"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminExamsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/exams/:subject/:exam"
                element={(
                  <ProtectedPageLayout
                    title="Exams"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminExamPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/permissions"
                element={(
                  <ProtectedPageLayout
                    title="Permissions"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminPermissionsPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/permission/:tag"
                element={(
                  <ProtectedPageLayout
                    title="Permissions"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminPermissionPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/payroll"
                element={(
                  <ProtectedPageLayout
                    title="Payroll"
                    authorisedGroups={[UserGroup.Managers]}
                  >
                    <AdminPayrollPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/timesheet"
                element={(
                  <ProtectedPageLayout
                    title="Timesheet"
                    authorisedGroups={[UserGroup.Managers]}
                  >
                    <AdminTimesheetPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/exam-assignment"
                element={(
                  <ProtectedPageLayout
                    title="Exam Assignment"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminExamAssignmentPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="roll"
                element={(
                  <ProtectedPageLayout
                    title="Roll"
                    authorisedGroups={[UserGroup.Admins, UserGroup.Teachers]}
                  >
                    <StaffRollPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="roll/:subject/:classId/:lesson/reschedule/:studentId"
                element={(
                  <ProtectedPageLayout
                    title="Roll"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminReschedulePage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="student/:subject/:classId/:lesson/reschedule"
                element={(
                  <ProtectedPageLayout
                    title="Reschedule"
                    authorisedGroups={[UserGroup.Students]}
                  >
                    <StudentReschedulePage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="teacher/classes"
                element={(
                  <ProtectedPageLayout
                    title="Classes"
                    authorisedGroups={[UserGroup.Teachers]}
                  >
                    <StaffClassesPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="teacher/class/:subject/:classId"
                element={(
                  <ProtectedPageLayout
                    title="Roll"
                    authorisedGroups={[UserGroup.Teachers]}
                  >
                    <TeacherClassPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="teacher/marking"
                element={(
                  <ProtectedPageLayout
                    title="Marking"
                    authorisedGroups={[UserGroup.Teachers]}
                  >
                    <TeacherMarkingPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="teacher/marking/:subject/:exam/:student/:attempt"
                element={(
                  <ProtectedPageLayout
                    title="Marking"
                    authorisedGroups={[UserGroup.Teachers, UserGroup.Admins]}
                  >
                    <TeacherMarkingPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="teacher/legacy-marking"
                element={(
                  <ProtectedPageLayout
                    title="Marking (Legacy)"
                    authorisedGroups={[UserGroup.Teachers]}
                  >
                    <LegacyExamsMarkingPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="teacher/legacy-marking/:subject/:exam/:student/:attempt"
                element={(
                  <ProtectedPageLayout
                    title="Marking (Legacy)"
                    authorisedGroups={[UserGroup.Teachers, UserGroup.Admins]}
                  >
                    <LegacyExamsMarkingPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="admin/printing"
                element={(
                  <ProtectedPageLayout
                    title="Printing"
                    authorisedGroups={[UserGroup.Admins]}
                  >
                    <AdminPrintingPage />
                  </ProtectedPageLayout>
                )}
              />
              <Route
                path="*"
                element={(
                  <ProtectedPageLayout
                    title="Error 404"
                  >
                    <NotFoundPage />
                  </ProtectedPageLayout>
                )}
              />
            </Routes>
          </BrowserRouter>
        </MUIThemeProvider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

const App = () => (
  <ReduxProvider store={store}>
    <ThemedApp />
  </ReduxProvider>
);

export default App;
